import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { BarChart3, Download, Radio } from "lucide-react";

type Stats = {
  mcps: number;
  downloads: number;
  active: number;
};

const INITIAL_STATS: Stats = {
  mcps: 312,
  downloads: 8741,
  active: 14,
};

const getStats = (): Stats => ({
  mcps: INITIAL_STATS.mcps,
  downloads: Math.floor(8700 + Math.random() * 80),
  active: Math.floor(12 + Math.random() * 8),
});

// Animated counter hook
function useCountUp(target: number, duration = 1400, enabled = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let start: number | null = null;
    const step = (timestamp: number) => {
      if (!start) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, enabled]);
  return value;
}

const StatCard = ({
  icon: Icon,
  value,
  label,
  suffix = "",
  color,
  delay,
}: {
  icon: React.ElementType;
  value: number;
  label: string;
  suffix?: string;
  color: string;
  delay: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const displayed = useCountUp(value, 1400, inView);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.55, delay }}
      className="glass-strong rounded-2xl p-8 card-lift shadow-premium text-center"
    >
      <div className={`w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="text-4xl font-bold text-foreground mb-1 tabular-nums tracking-tight">
        {displayed.toLocaleString()}{suffix}
      </div>
      <div className="text-sm text-muted-foreground font-medium">{label}</div>
    </motion.div>
  );
};

const LiveStats = () => {
  const [stats, setStats] = useState<Stats>(INITIAL_STATS);
  const viteEnv = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
  const isLocalTestMode = viteEnv?.VITE_LOCAL_TEST_MODE === "true";

  useEffect(() => {
    const updateStats = async () => {
      if (!isLocalTestMode) {
        setStats(getStats());
        return;
      }
      try {
        const response = await fetch("/api/stats");
        if (!response.ok) throw new Error("Failed to load live stats");
        const json = (await response.json()) as {
          totalMcps: number;
          totalDownloads: number;
          activeUsers: number;
        };
        setStats({ mcps: json.totalMcps, downloads: json.totalDownloads, active: json.activeUsers });
      } catch {
        setStats(getStats());
      }
    };
    updateStats();
    const intervalId = window.setInterval(updateStats, 5000);
    return () => window.clearInterval(intervalId);
  }, [isLocalTestMode]);

  return (
    <section className="max-w-5xl mx-auto px-6 py-20">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.55 }}
        className="text-center mb-12"
      >
        <div className="inline-flex items-center gap-2 mb-4">
          <span className="pulse-dot" />
          <span className="text-xs font-semibold text-primary uppercase tracking-widest">Live Stats</span>
        </div>
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
          Trusted by Developers Worldwide
        </h2>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <StatCard
          icon={BarChart3}
          value={stats.mcps}
          label="MCPs Created"
          color="bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400"
          delay={0}
        />
        <StatCard
          icon={Download}
          value={stats.downloads}
          label="API Endpoints Converted"
          color="bg-violet-50 text-violet-600 dark:bg-violet-950 dark:text-violet-400"
          delay={0.1}
        />
        <StatCard
          icon={Radio}
          value={stats.active}
          label="Converting Right Now"
          suffix="+"
          color="bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400"
          delay={0.2}
        />
      </div>
    </section>
  );
};

export default LiveStats;
